import { checkStatus, parseJSON } from "@utils/fetch";
import { insertAITemplateGeneratorDataInDB } from "database/supabase/freeTools";
import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import TemplateGeneratorForm, { TemplateGeneratorFormInputProps } from "./TemplateGeneratorForm";
import TemplatePreview from "./TemplatePreview";

export interface APIResponseDataProps {
    header?: string;
    body: string;
    footer?: string;
}

interface AIWhatsappTemplateGeneratorProps {
    focusToForm: React.RefObject<HTMLDivElement>;
}
const siteKey = process.env.NEXT_PUBLIC_API_RECAPTCHA_SITE_KEY || "";

const AIWhatsappTemplateGenerator: React.FC<AIWhatsappTemplateGeneratorProps> = (props) => {
    const { focusToForm } = props;

    const [errorMessage, setErrorMessage] = useState(false);
    const [loadingState, setLoadingState] = useState<"generate" | "regenerate" | null>(null);

    const [formData, setFormData] = useState<TemplateGeneratorFormInputProps>({
        businessName: "Gallabox",
        businessBio: "Business Account",
        messageText: "",
        messageTone: "Creative",
        addEmoji: true,
    });

    const [previewData, setPreviewData] = useState<APIResponseDataProps>({
        header: "Welcome to Gallabox",
        body: `Hi Maria,

        We are excited to have you on board!
        
        Level up your customer engagement and increase your revenues—all within WhatsApp—using Gallabox.`,
        footer: "Reply with 'STOP' to unsubscribe from marketing messages.",
    });

    const [showRegenerateBtn, setShowRegenerateBtn] = useState<boolean>(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const handleRecaptchaChange = (token: string | null) => {
        recaptchaRef.current?.reset();
        onGenerateApi(formData, token);
    };

    const onGenerate = (formData: TemplateGeneratorFormInputProps): void => {
        setFormData(formData);
        setLoadingState(formData.loadingState ?? null);
        recaptchaRef.current?.execute();
    };

    const onGenerateApi = async (formData: TemplateGeneratorFormInputProps, recaptchaToken: string | null) => {
        try {
            const API_URL = "/api/v1/templategenerator/getTemplate?" + new URLSearchParams(formData as unknown as any).toString();
            const response = await fetch(API_URL, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${recaptchaToken}`,
                },
            })
                .then(checkStatus)
                .then(parseJSON);
            setFormData(formData);
            setPreviewData(response);
            setShowRegenerateBtn(true);
            setErrorMessage(false);
        } catch (error) {
            setErrorMessage(true);
        } finally {
            const { loadingState, ...rest } = formData;
            const payload = { ...rest, mode: loadingState };
            insertAITemplateGeneratorDataInDB(payload);
            setLoadingState(null);
        }
    };

    return (
        <div className="lg:max-w-6xl lg:mx-auto w-full mx-0 flex lg:flex-row flex-col lg:gap-8 gap-14">
            <TemplateGeneratorForm
                buttonText="Generate My Template"
                onGenerate={onGenerate}
                templateGeneratorRef={focusToForm}
                errorMessage={errorMessage}
                isGeneratingTemplate={loadingState === "generate"}
            />
            <TemplatePreview
                formData={formData}
                previewData={previewData}
                messageContent={
                    <>
                        {previewData.header && (
                            <>
                                <h6 className="text-base font-bold text-gray-800">{previewData.header}</h6>
                                <br />
                            </>
                        )}

                        <p className="whitespace-pre-line">{previewData.body}</p>
                        <br />
                        {previewData.footer && (
                            <em>
                                <footer className="text-sm font-normal text-gray-400">{previewData.footer}</footer>
                            </em>
                        )}
                    </>
                }
                onGenerate={onGenerate}
                showRegenerateBtn={showRegenerateBtn}
                isRegeneratingTemplate={loadingState === "regenerate"}
            />
            {/* @ts-expect-error react-google-recaptcha ref typing does not match the forwarded ref instance */}
            <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} size="invisible" onChange={handleRecaptchaChange} />
        </div>
    );
};

export default AIWhatsappTemplateGenerator;
